import { influencer } from '@round/api';
import { functionalUpdate, SortingState } from '@tanstack/react-table';
import { VirtualizedTable, tableHelpers } from '@round/creatorbase-ui';
import { CreatorTableEmptyState } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/EmptyState/CreatorTableEmptyState';
import { Column, PAGE_SIZE } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { AddToPoolCell } from './components/AddToPoolCell/AddToPoolCell';
import { SelectAllCreatorsHeader, SelectCreatorCell } from './components/SelectCreatorCell/SelectCreatorCell';
import { Meta, ROW_HEIGHT_PX, getCreatorColumns } from './helpers';
import styles from './AddCreatorsTable.module.css';

type Props = {
    data: influencer.CreatorSearchResult[];
    meta: Meta;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    onEndReached: () => void;
    onCreatorToggle: (creator: influencer.CreatorSearchResult) => void;
    onSelectAll: (creators: influencer.CreatorSearchResult[]) => void;
    onDeselectAll: (creators: influencer.CreatorSearchResult[]) => void;
    className?: string;
};

export const AddCreatorsTable = ({
    data,
    meta,
    sorting,
    onSortingChange,
    onEndReached,
    onCreatorToggle,
    onSelectAll,
    onDeselectAll,
    className,
}: Props) => {
    const columns: Column[] = [
        {
            id: 'select',
            meta: { className: styles.selectColumn },
            header: (context) => (
                <SelectAllCreatorsHeader {...context} onSelectAll={onSelectAll} onDeselectAll={onDeselectAll} />
            ),
            cell: (context) => <SelectCreatorCell {...context} onToggle={onCreatorToggle} />,
            size: 50,
            enableSorting: false,
        },
        ...getCreatorColumns(),
        {
            header: undefined,
            id: 'actions',
            cell: AddToPoolCell,
            enableSorting: false,
            // Holds the add button alongside an icon per platform account already in the pool.
            size: 140,
        },
    ];

    return (
        <VirtualizedTable
            columns={columns}
            data={tableHelpers.getLoadingRows(data, { pageSize: PAGE_SIZE, isLoading: meta.isLoading })}
            meta={meta}
            virtualizerOptions={{
                estimateSize: () => ROW_HEIGHT_PX,
                getItemKey: (index) => `${index}-${data[index]?._id}` || `skeleton-${index}`,
                overscan: 50,
            }}
            onEndReached={onEndReached}
            containerClassName={className}
            columnPinning={{ left: ['select', 'creator'] }}
            enableSorting
            manualSorting
            sorting={sorting}
            onSortingChange={(newSorting) => onSortingChange(functionalUpdate(newSorting, sorting))}
            noDataLabel={<CreatorTableEmptyState hasError={meta.hasError} />}
        />
    );
};
